import { useMemo } from 'react'
import { MALDIVES_BUNDLE_DEFAULT_GUESTS } from 'constants/charterFlight'
import type { FlightsClassTypes } from 'constants/flight'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { getCharterBundleSearchTilePricing } from 'components/SearchKit/SearchTiles/accommodations/charterBundleSearchTilePricing'
import type { CalendarItemBundleData } from './types'
import { getCheapestCalendarBundleData } from '../utils/bundlePricingDisplay'
import { useMaldivesDirectCalendarPricing } from './useMaldivesDirectCalendarPricing'
import {
  buildMaldivesCheapestStreamSearchFilters,
  mapCheapestTilePricingToBundleData,
} from './maldivesCheapestStreamSearchPrice'

interface UseCheapestBundlePriceParams {
  checkIn?: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  useMemberPrice: boolean
  disabled?: boolean
  /**
   * When true and no checkIn is set, use streamed search metadata `lowestPrice`
   * (same as /campaign/maldives-direct?sortBy=price.asc tiles). Opt-in only —
   * other anytime banners keep calendar-derived pricing.
   */
  useStreamSearchForAnytime?: boolean
}

const EMPTY_BUNDLE_DATA: CalendarItemBundleData = {
  fetching: false,
  available: false,
  totalPrice: 0,
  pricePerPerson: 0,
  offerCount: 0,
}

/**
 * Banner "from" price for Maldives charter bundles.
 *
 * Streamed search path (opt-in via useStreamSearchForAnytime, anytime only):
 * same source as the first tile on /campaign/maldives-direct?sortBy=price.asc.
 *
 * Default / dated path: calendar hotel + flight pricing (cheapest date, or
 * the selected checkIn when provided).
 */
export function useCheapestBundlePrice(params: UseCheapestBundlePriceParams): CalendarItemBundleData {
  const {
    checkIn,
    nights,
    rooms,
    fareClass,
    charterBundleRoute,
    useMemberPrice,
    disabled = false,
    useStreamSearchForAnytime = false,
  } = params

  const useSearchTilePrice = useStreamSearchForAnytime && !checkIn
  const guestCount = sumUpOccupancies(rooms) || MALDIVES_BUNDLE_DEFAULT_GUESTS
  const searchFilters = useMemo(
    () => {
      if (disabled || !useSearchTilePrice) {
        return undefined
      }
      return buildMaldivesCheapestStreamSearchFilters({ rooms, nights, fareClass })
    },
    [disabled, useSearchTilePrice, rooms, fareClass, nights],
  )

  const offerList = useOfferList(searchFilters, {
    minRequiredOffers: 1,
    disabled: !searchFilters,
  })

  const firstOfferId = offerList.offerIds[0] ?? ''
  const offerMetaData = useOfferMetaData(firstOfferId, searchFilters ?? EmptyObject)
  const tilePricing = getCharterBundleSearchTilePricing({
    charterSearchPrices: offerMetaData?.pricing,
  })

  // Calendar path: Hotel Search / offer-page banners (default / dated).
  const { dateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass,
    disabled: disabled || useSearchTilePrice || !charterBundleRoute,
  })

  const { calendarPricingByDate, isLoading: calendarLoading } = useMaldivesDirectCalendarPricing({
    rooms,
    fareClass,
    nights,
    charterBundleRoute,
    dateRangesByMonth,
    disabled: disabled || useSearchTilePrice,
  })

  return useMemo(() => {
    if (disabled) {
      return EMPTY_BUNDLE_DATA
    }

    if (useSearchTilePrice) {
      return mapCheapestTilePricingToBundleData({
        tilePricing,
        guestCount,
        useMemberPrice,
        fetching: offerList.fetching,
        waitingForMetaData: !offerMetaData,
      })
    }

    const calendarEntry = checkIn
      ? calendarPricingByDate[checkIn]
      : getCheapestCalendarBundleData(calendarPricingByDate, { useMemberPrice, priceMode: 'total' })

    if (!calendarEntry) {
      return {
        ...EMPTY_BUNDLE_DATA,
        fetching: calendarLoading,
      }
    }

    return calendarEntry
  }, [
    disabled,
    useSearchTilePrice,
    offerList.fetching,
    tilePricing,
    offerMetaData,
    guestCount,
    checkIn,
    calendarPricingByDate,
    calendarLoading,
    useMemberPrice,
  ])
}
